import { useEffect, useRef, useState, useCallback } from 'react';
import { APP_VERSION } from '@/config/version';

/**
 * Heartbeat payload structure
 */
interface HeartbeatPayload {
  timestamp: string;
  version: string;
  userAgent: string;
  environment: string;
}

/**
 * Heartbeat hook return type
 */
interface UseHeartbeatReturn {
  isEnabled: boolean;
  lastHeartbeat: Date | null;
  isPaused: boolean;
}

/**
 * Custom hook for GlitchTip heartbeat monitoring
 *
 * Sends periodic "heartbeat" signals to GlitchTip to monitor frontend uptime.
 *
 * Features:
 * - Sends heartbeat every 10 minutes when tab is active
 * - Pauses when tab is hidden (using Page Visibility API)
 * - Resumes when tab becomes visible again
 * - Respects user privacy (no sensitive data)
 * - Handles errors silently
 * - Only enabled if VITE_GLITCHTIP_HEARTBEAT_ENABLED is 'true'
 *
 * @example
 * ```tsx
 * function App() {
 *   useHeartbeat();
 *   return <div>...</div>;
 * }
 * ```
 */
export function useHeartbeat(): UseHeartbeatReturn {
  const [isEnabled, setIsEnabled] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check if heartbeat is configured
  const heartbeatUrl = import.meta.env.VITE_GLITCHTIP_HEARTBEAT_URL;
  const heartbeatEnabled =
    import.meta.env.VITE_GLITCHTIP_HEARTBEAT_ENABLED === 'true';
  const appVersion = APP_VERSION;
  const environment =
    import.meta.env.VITE_GLITCHTIP_ENVIRONMENT || 'development';

  /**
   * Send heartbeat to GlitchTip
   */
  const sendHeartbeat = useCallback(async (): Promise<void> => {
    // Skip if not enabled or URL not configured
    if (!heartbeatEnabled || !heartbeatUrl) {
      return;
    }

    try {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      const payload: HeartbeatPayload = {
        timestamp: new Date().toISOString(),
        version: appVersion,
        userAgent: navigator.userAgent,
        environment,
      };

      const response = await fetch(heartbeatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Heartbeat failed with status ${response.status}`);
      }

      // Update last heartbeat timestamp
      setLastHeartbeat(new Date());

      // Log in development only
      if (import.meta.env.DEV) {
        console.log('[Heartbeat] Sent successfully:', {
          timestamp: payload.timestamp,
          version: payload.version,
          environment: payload.environment,
        });
      }
    } catch (error) {
      // Silently handle errors (don't show to user)
      // Only log in development
      if (
        import.meta.env.DEV &&
        error instanceof Error &&
        error.name !== 'AbortError'
      ) {
        console.error('[Heartbeat] Failed to send:', error.message);
      }
    }
  }, [heartbeatEnabled, heartbeatUrl, appVersion, environment]);

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback((): void => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval (10 minutes = 600000ms)
    intervalRef.current = setInterval(
      () => {
        sendHeartbeat();
      },
      10 * 60 * 1000
    );

    setIsPaused(false);

    if (import.meta.env.DEV) {
      console.log('[Heartbeat] Started (interval: 10 minutes)');
    }
  }, [sendHeartbeat]);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback((): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsPaused(true);

    if (import.meta.env.DEV) {
      console.log('[Heartbeat] Stopped');
    }
  }, []);

  useEffect(() => {
    // Skip if not enabled or URL not configured
    if (!heartbeatEnabled || !heartbeatUrl) {
      if (import.meta.env.DEV && heartbeatEnabled && !heartbeatUrl) {
        console.warn(
          '[Heartbeat] Enabled but VITE_GLITCHTIP_HEARTBEAT_URL not configured'
        );
      }
      return;
    }

    setIsEnabled(true);

    // Start heartbeat monitoring
    startHeartbeat();

    // Handle Page Visibility API
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - pause heartbeat
        stopHeartbeat();
        if (import.meta.env.DEV) {
          console.log('[Heartbeat] Tab hidden - paused');
        }
      } else {
        // Tab is visible - resume heartbeat
        startHeartbeat();
        if (import.meta.env.DEV) {
          console.log('[Heartbeat] Tab visible - resumed');
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      stopHeartbeat();
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (import.meta.env.DEV) {
        console.log('[Heartbeat] Cleanup completed');
      }
    };
  }, [heartbeatEnabled, heartbeatUrl, startHeartbeat, stopHeartbeat]);

  return {
    isEnabled,
    lastHeartbeat,
    isPaused,
  };
}
